// Event Types
export type EventStatus = 'scheduled' | 'active' | 'completed' | 'cancelled';
export type EventMode = 'sync' | 'async' | 'hybrid';
export type GuestStatus = 'invited' | 'accepted' | 'declined' | 'joined' | 'completed';
export type TastingStep = 'intro' | 'aroma' | 'taste' | 'finish' | 'botanicals' | 'summary';

export interface EventBranding {
  id: number;
  event_id: number;
  logo_url?: string;
  cover_image_url?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  font_family?: string;
  custom_css?: string;
  intro_text?: string;
  outro_text?: string;
  use_venue_defaults: boolean;
}

export interface EventSpirit {
  spirit_name: string;
  spirit_brand?: string;
  tasting_order: number;
}

export interface VenueTastingEvent {
  uuid: string;
  name: string;
  description?: string;
  scheduled_at?: string;
  tasting_mode: EventMode;
  max_participants: number;
  current_participants: number;
  status: EventStatus;
  access_code: string;
  venue_name?: string;
  host_name?: string;
  spirits?: EventSpirit[];
  branding?: EventBranding;
}

export interface EventGuest {
  id: number;
  event_id: number;
  session_id?: number;
  email?: string;
  name?: string;
  invitation_token?: string;
  status: GuestStatus;
  is_anonymous: boolean;
  invited_at?: string;
  accepted_at?: string;
  joined_at?: string;
  completed_at?: string;
  current_step?: TastingStep;
}

export interface EventStatusResponse {
  status: EventStatus;
  current_step: TastingStep;
  is_locked: boolean;
  moderator_message?: string;
  participant_count: number;
}

export interface InvitationResponse {
  event_name: string;
  event_uuid: string;
  venue_name?: string;
  scheduled_at?: string;
  event_status: EventStatus;
  guest_status: GuestStatus;
  branding?: EventBranding;
}

// Tasting Session Types
export interface TastingSession {
  uuid: string;
  venue_id: number;
  tasting_type: string;
  status: 'active' | 'completed' | 'abandoned';
  current_step: TastingStep;
  started_at: string;
  completed_at?: string;
  event_id?: number;
  guest_id?: number;
  spirits?: SpiritTasting[];
}

export interface SpiritTasting {
  id: number;
  session_id: number;
  spirit_id?: number;
  spirit_name: string;
  spirit_brand?: string;
  tasting_order: number;
  aroma_rating?: number;
  aroma_notes?: string;
  aroma_descriptors?: string[];
  taste_rating?: number;
  taste_notes?: string;
  taste_descriptors?: string[];
  finish_rating?: number;
  finish_notes?: string;
  finish_descriptors?: string[];
  detected_botanicals?: string[];
  overall_rating?: number;
  personal_notes?: string;
}

// Descriptor Categories
export interface DescriptorCategory {
  name: string;
  descriptors: string[];
}

export const AROMA_DESCRIPTORS: DescriptorCategory[] = [
  { name: 'Zitrus', descriptors: ['Zitrone', 'Limette', 'Orange', 'Grapefruit', 'Bergamotte'] },
  { name: 'Floral', descriptors: ['Rose', 'Lavendel', 'Jasmin', 'Holunderblüte', 'Veilchen'] },
  { name: 'Kräuter', descriptors: ['Wacholder', 'Koriander', 'Rosmarin', 'Thymian', 'Minze'] },
  { name: 'Gewürze', descriptors: ['Pfeffer', 'Kardamom', 'Zimt', 'Ingwer', 'Anis'] },
  { name: 'Erdig', descriptors: ['Wurzel', 'Erde', 'Moos', 'Pilz', 'Holz'] },
];

export const TASTE_DESCRIPTORS: DescriptorCategory[] = [
  { name: 'Süß', descriptors: ['Honig', 'Vanille', 'Karamell', 'Frucht', 'Malz'] },
  { name: 'Bitter', descriptors: ['Zitrusschale', 'Kräuter', 'Tonic', 'Hopfen', 'Kaffee'] },
  { name: 'Würzig', descriptors: ['Pfeffer', 'Ingwer', 'Wacholder', 'Gewürznelke', 'Muskat'] },
  { name: 'Frisch', descriptors: ['Gurke', 'Minze', 'Zitrus', 'Grüner Tee', 'Aloe'] },
  { name: 'Cremig', descriptors: ['Butter', 'Sahne', 'Nuss', 'Kokos', 'Milch'] },
];

export const FINISH_DESCRIPTORS: DescriptorCategory[] = [
  { name: 'Länge', descriptors: ['Kurz', 'Mittel', 'Lang', 'Sehr lang', 'Anhaltend'] },
  { name: 'Charakter', descriptors: ['Warm', 'Kühl', 'Scharf', 'Sanft', 'Komplex'] },
  { name: 'Nachgeschmack', descriptors: ['Zitrus', 'Kräuter', 'Gewürze', 'Blumen', 'Süß'] },
];

export const COMMON_BOTANICALS = [
  'Wacholder', 'Koriander', 'Angelikawurzel', 'Zitronenschale', 'Orangenschale',
  'Kardamom', 'Zimt', 'Iris', 'Lavendel', 'Rose', 'Pfeffer', 'Kubebenpfeffer',
  'Ingwer', 'Süßholz', 'Mandel', 'Fenchel', 'Anis', 'Cassia', 'Muskatnuss',
  'Gurke', 'Basilikum', 'Rosmarin', 'Thymian', 'Salbei', 'Minze',
];

// Prepared Tasting Types
export interface PreparedTastingSpirit {
  id: number;
  menu_item_id: number;
  tasting_order: number;
  spirit_name: string;
  spirit_brand?: string;
  price?: number;
}

export interface PreparedTasting {
  id: number;
  uuid: string;
  venue_id: number;
  template_id?: number;
  template_name?: string;
  table_number?: string;
  guest_name?: string;
  notes?: string;
  status: 'pending' | 'started' | 'completed' | 'expired';
  expires_at: string;
  spirits: PreparedTastingSpirit[];
  spirit_count: number;
  link: string;
  created_at: string;
  session_uuid?: string; // UUID of the tasting session if started
}

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  pagination?: {
    total: number;
    limit: number;
    offset: number;
  };
}
